import React, { useState, useEffect } from 'react';
import {
  FileText, DollarSign, Clock, Send, CheckCircle, Download,
  Plus, Calendar, TrendingUp, Loader2, Search, Eye, Edit, Trash2,
  AlertCircle, Users, Briefcase
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import InvoiceSuggestions from '../components/lexy/InvoiceSuggestions';
import { useTranslation } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicesPageNew = () => {
  const navigate = useNavigate();
  const { language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [suggestionStats, setSuggestionStats] = useState({
    total_unbilled_hours: 0,
    total_unbilled_amount: 0,
    suggestion_count: 0
  });
  const [stats, setStats] = useState({
    unbilled: 0,
    this_month: 0,
    overdue: 0,
    total_outstanding: 0
  });
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'draft', 'sent', 'paid'
  const [searchQuery, setSearchQuery] = useState('');

  // Translations
  const texts = {
    title: language === 'en' ? 'Invoicing' : 'Fakturering',
    inUnbilledWork: language === 'en' ? 'in unbilled work' : 'i ufakturert arbeid',
    createInvoice: language === 'en' ? 'Create Invoice' : 'Opprett faktura',
    selectCase: language === 'en' ? 'Select a case with unbilled time below' : 'Velg en sak med ufakturert tid nedenfor',
    unbilled: language === 'en' ? 'Unbilled' : 'Ufakturert',
    thisMonth: language === 'en' ? 'This Month' : 'Denne måneden',
    awaitingPayment: language === 'en' ? 'Awaiting Payment' : 'Venter på betaling',
    overdue: language === 'en' ? 'Overdue' : 'Forfalt',
    clients: language === 'en' ? 'clients' : 'klienter',
    invoiced: language === 'en' ? 'Invoiced' : 'Fakturert',
    outstanding: language === 'en' ? 'Outstanding' : 'Utestående',
    needsFollowUp: language === 'en' ? 'Needs follow-up' : 'Må følges opp',
    allInvoices: language === 'en' ? 'All Invoices' : 'Alle fakturaer',
    draft: language === 'en' ? 'Draft' : 'Utkast',
    sent: language === 'en' ? 'Sent' : 'Sendt',
    paid: language === 'en' ? 'Paid' : 'Betalt',
    searchPlaceholder: language === 'en' ? 'Search for invoice number, case or client...' : 'Søk etter fakturanummer, sak eller klient...',
    noInvoicesFound: language === 'en' ? 'No invoices found' : 'Ingen fakturaer funnet',
    noInvoicesYet: language === 'en' ? 'No invoices yet' : 'Ingen fakturaer ennå',
    tryOtherSearch: language === 'en' ? 'Try searching with other keywords' : 'Prøv å søke med andre søkeord',
    createFirstInvoice: language === 'en' ? 'Create your first invoice from a case with registered hours' : 'Opprett din første faktura fra en sak med registrerte timer',
    seeCasesWithUnbilled: language === 'en' ? 'See cases with unbilled time' : 'Se saker med ufakturert tid',
    statusDraft: language === 'en' ? 'Draft' : 'Utkast',
    statusSent: language === 'en' ? 'Sent' : 'Sendt',
    statusPaid: language === 'en' ? 'Paid' : 'Betalt',
    statusOverdue: language === 'en' ? 'Overdue' : 'Forfalt',
    statusCancelled: language === 'en' ? 'Cancelled' : 'Kansellert',
    couldNotLoad: language === 'en' ? 'Could not load invoices' : 'Kunne ikke laste fakturaer'
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Load invoices and suggestions
      const [invoicesRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/invoices/`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/invoices/suggestions/unbilled`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { suggestions: [], total_unbilled_hours: 0, total_unbilled_amount: 0, suggestion_count: 0 } }))
      ]);

      setInvoices(invoicesRes.data.invoices || []);
      setSuggestionStats({
        total_unbilled_hours: suggestionsRes.data.total_unbilled_hours || 0,
        total_unbilled_amount: suggestionsRes.data.total_unbilled_amount || 0,
        suggestion_count: suggestionsRes.data.suggestion_count || 0
      });
      
      // Calculate stats from invoices
      const invoiceList = invoicesRes.data.invoices || [];
      
      const thisMonthTotal = invoiceList
        .filter(inv => {
          const date = new Date(inv.created_at);
          const now = new Date();
          return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        })
        .reduce((sum, inv) => sum + (inv.total || inv.total_amount || 0), 0);
      
      const overdueTotal = invoiceList
        .filter(inv => inv.status === 'OVERDUE')
        .reduce((sum, inv) => sum + (inv.total || inv.total_amount || 0), 0);
      
      const outstandingTotal = invoiceList
        .filter(inv => inv.status === 'SENT' || inv.status === 'OVERDUE')
        .reduce((sum, inv) => sum + (inv.total || inv.total_amount || 0), 0);

      setStats({
        unbilled: suggestionsRes.data.total_unbilled_amount || 0,
        this_month: thisMonthTotal,
        overdue: overdueTotal,
        total_outstanding: outstandingTotal
      });
    } catch (error) {
      console.error('Failed to load invoicing data:', error);
      toast.error(texts.couldNotLoad);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'en' ? 'en-US' : 'nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'bg-slate-100 text-slate-700',
      SENT: 'bg-blue-100 text-blue-700',
      PAID: 'bg-green-100 text-green-700',
      OVERDUE: 'bg-red-100 text-red-700',
      CANCELLED: 'bg-slate-100 text-slate-500'
    };
    return colors[status] || colors.DRAFT;
  };

  const getStatusLabel = (status) => {
    const labels = {
      DRAFT: texts.statusDraft,
      SENT: texts.statusSent,
      PAID: texts.statusPaid,
      OVERDUE: texts.statusOverdue,
      CANCELLED: texts.statusCancelled
    };
    return labels[status] || status;
  };

  const filteredInvoices = invoices.filter(inv => {
    if (activeTab === 'draft') return inv.status === 'DRAFT';
    if (activeTab === 'sent') return inv.status === 'SENT' || inv.status === 'OVERDUE';
    if (activeTab === 'paid') return inv.status === 'PAID';
    return true; // overview shows all
  }).filter(inv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(query) ||
      inv.case_title?.toLowerCase().includes(query) ||
      inv.recipient_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{texts.title}</h1>
              <p className="text-slate-600 mt-1">
                {formatCurrency(stats.unbilled)} {texts.inUnbilledWork}
              </p>
            </div>
            <button
              onClick={() => {
                toast.info(texts.selectCase);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {texts.createInvoice}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-50 rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">{texts.unbilled}</span>
              <Clock className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.unbilled)}</p>
            <p className="text-xs text-slate-500 mt-1">{suggestionStats.suggestion_count} {texts.clients}</p>
          </div>

          <div className="bg-gray-50 rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">{texts.thisMonth}</span>
              <TrendingUp className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.this_month)}</p>
            <p className="text-xs text-slate-500 mt-1">{texts.invoiced}</p>
          </div>

          <div className="bg-gray-50 rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">{texts.awaitingPayment}</span>
              <Send className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.total_outstanding)}</p>
            <p className="text-xs text-slate-500 mt-1">{texts.outstanding}</p>
          </div>

          <div className="bg-gray-50 rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">{texts.overdue}</span>
              <AlertCircle className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.overdue)}</p>
            <p className="text-xs text-slate-500 mt-1">{texts.needsFollowUp}</p>
          </div>
        </div>

        {/* Invoice Suggestions - AI powered unbilled time */}
        <div className="mb-8">
          <InvoiceSuggestions onInvoiceCreated={loadData} />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-t-lg border border-slate-200 border-b-0">
          <div className="flex gap-1 p-1">
            {[
              { id: 'overview', label: texts.allInvoices, count: invoices.length },
              { id: 'draft', label: texts.draft, count: invoices.filter(i => i.status === 'DRAFT').length },
              { id: 'sent', label: texts.sent, count: invoices.filter(i => i.status === 'SENT' || i.status === 'OVERDUE').length },
              { id: 'paid', label: texts.paid, count: invoices.filter(i => i.status === 'PAID').length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                {tab.label}
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  activeTab === tab.id ? 'bg-slate-700' : 'bg-slate-200 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 border-t-0 px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={texts.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Invoices List */}
        <div className="bg-white rounded-b-lg border border-slate-200 border-t-0">
          {filteredInvoices.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchQuery ? texts.noInvoicesFound : texts.noInvoicesYet}
              </h3>
              <p className="text-slate-600 mb-6">
                {searchQuery 
                  ? texts.tryOtherSearch
                  : texts.createFirstInvoice
                }
              </p>
              {!searchQuery && suggestionStats.suggestion_count > 0 && (
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
                >
                  {texts.seeCasesWithUnbilled}
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="px-6 py-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-medium text-slate-900">
                            {invoice.invoice_number || `#${invoice.id.slice(0, 8)}`}
                          </h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                            {getStatusLabel(invoice.status)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {invoice.case_title} • {invoice.recipient_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-semibold text-slate-900">
                          {formatCurrency(invoice.total_amount)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatDate(invoice.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                          <Eye className="w-4 h-4 text-slate-600" />
                        </button>
                        <button type="button" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                          <Download className="w-4 h-4 text-slate-600" />
                        </button>
                        {invoice.status === 'DRAFT' && (
                          <button type="button" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                            <Edit className="w-4 h-4 text-slate-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicesPageNew;
